// src/pages/app/PoliciesPage.jsx — Policy & Tier Configuration (Admin only)
import { useEffect, useState } from 'react';
import { policiesApi } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Save, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

const GRADES = ['L1', 'L2', 'L3', 'L4', 'L5'];
const CITY_TIERS = ['tier1', 'tier2', 'tier3'];
const FLIGHT_CLASSES = ['economy', 'premium_economy', 'business', 'first'];

const GRADE_LABELS = {
  L1: 'Grade L1 — Executive / CXO (Business Class, Up to ₹15k/night Hotel)',
  L2: 'Grade L2 — Senior Manager / Director (Premium Economy, Up to ₹9k/night Hotel)',
  L3: 'Grade L3 — Senior Professional / Specialist (Economy, Up to ₹5k/night Hotel)',
  L4: 'Grade L4 — Mid-Level Staff / Engineer (Economy, Up to ₹3.5k/night Hotel)',
  L5: 'Grade L5 — Junior Staff / Associate (Economy, Up to ₹2.5k/night Hotel)'
};
const CITY_LABELS = { tier1: 'Tier 1 (Mumbai/Delhi/Bangalore)', tier2: 'Tier 2 (Pune/Hyderabad)', tier3: 'Tier 3 (Others)' };

const emptyPolicy = { grade: 'L3', city_tier: 'tier1', max_hotel_rate_per_night: 5000, flight_class: 'economy', per_diem_rate: 500, max_meal_per_day: 300, max_local_transit: 200 };

const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white";

export default function PoliciesPage() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyPolicy);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await policiesApi.list();
      setPolicies(data);
    } catch { toast.error('Failed to load policies'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        max_hotel_rate_per_night: parseFloat(form.max_hotel_rate_per_night),
        per_diem_rate: parseFloat(form.per_diem_rate),
        max_meal_per_day: parseFloat(form.max_meal_per_day),
        max_local_transit: parseFloat(form.max_local_transit),
      };
      if (editingId) {
        await policiesApi.update(editingId, payload);
        toast.success('Policy updated!');
      } else {
        await policiesApi.create(payload);
        toast.success('Policy created!');
      }
      setShowForm(false); setEditingId(null); setForm(emptyPolicy);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleEdit = (policy) => {
    setForm({ ...policy });
    setEditingId(policy.id);
    setShowForm(true);
  };

  const [deletePolicyId, setDeletePolicyId] = useState(null);
  const [showDeletePolicyModal, setShowDeletePolicyModal] = useState(false);

  const handleDeletePolicyClick = (id) => {
    setDeletePolicyId(id);
    setShowDeletePolicyModal(true);
  };

  const handleConfirmDeletePolicy = async () => {
    if (!deletePolicyId) return;
    try {
      await policiesApi.delete(deletePolicyId);
      toast.success('Policy deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
    finally {
      setShowDeletePolicyModal(false);
      setDeletePolicyId(null);
    }
  };

  // Group by grade
  const grouped = GRADES.reduce((acc, g) => {
    acc[g] = policies.filter(p => p.grade === g);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900">Policy & Tier Configuration</h1>
          <p className="text-slate-500 text-sm mt-1">Configure travel entitlements per employee grade and city tier</p>
        </div>
        <button onClick={() => { setShowForm(!showForm); setEditingId(null); setForm(emptyPolicy); }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors">
          <Plus className="w-4 h-4" /> Add Policy Tier
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm sm:text-base">{editingId ? 'Edit Policy Tier' : 'New Policy Tier'}</CardTitle>
              <button onClick={() => { setShowForm(false); setEditingId(null); setForm(emptyPolicy); }}
                className="p-1 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Employee Grade</label>
                <Select
                  value={form.grade}
                  onValueChange={(val) => setForm(f => ({ ...f, grade: val }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GRADES.map(g => (
                      <SelectItem key={g} value={g}>
                        {GRADE_LABELS[g]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">City Tier</label>
                <Select
                  value={form.city_tier}
                  onValueChange={(val) => setForm(f => ({ ...f, city_tier: val }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CITY_TIERS.map(t => (
                      <SelectItem key={t} value={t}>
                        {CITY_LABELS[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Flight Class Entitlement</label>
                <Select
                  value={form.flight_class}
                  onValueChange={(val) => setForm(f => ({ ...f, flight_class: val }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FLIGHT_CLASSES.map(fc => (
                      <SelectItem key={fc} value={fc} className="capitalize">
                        {fc.replace('_', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Max Hotel Rate/Night (₹)</label>
                <input type="number" min="0" value={form.max_hotel_rate_per_night}
                  onChange={e => setForm(f => ({ ...f, max_hotel_rate_per_night: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Per Diem Rate/Day (₹)</label>
                <input type="number" min="0" value={form.per_diem_rate}
                  onChange={e => setForm(f => ({ ...f, per_diem_rate: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Max Meal/Day (₹)</label>
                <input type="number" min="0" value={form.max_meal_per_day}
                  onChange={e => setForm(f => ({ ...f, max_meal_per_day: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Max Local Transit (₹)</label>
                <input type="number" min="0" value={form.max_local_transit}
                  onChange={e => setForm(f => ({ ...f, max_local_transit: e.target.value }))} className={inputCls} />
              </div>
              <div className="sm:col-span-3 flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }}
                  className="px-5 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={saving}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {editingId ? 'Update Policy' : 'Save Policy'}
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Policy Grid by Grade */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" /></div>
      ) : (
        GRADES.map(grade => (
          grouped[grade]?.length > 0 && (
            <Card key={grade} className="border-slate-200 bg-white overflow-hidden shadow-sm">
              <CardHeader className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 border-b bg-slate-50/50">
                <CardTitle className="text-sm sm:text-base font-bold text-slate-900">{GRADE_LABELS[grade]}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="bg-slate-50/80 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        {['City Tier', 'Flight Class', 'Max Hotel/Night', 'Per Diem/Day', 'Max Meal/Day', 'Max Transit', 'Actions'].map((h, i) => (
                          <th key={h} className={cn("py-2 px-3 sm:py-2.5 sm:px-4 lg:py-3.5 lg:px-6", i === 6 ? "text-right" : "")}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {grouped[grade].map(policy => (
                        <tr key={policy.id} className="hover:bg-slate-50/80 transition-colors group">
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-semibold capitalize text-slate-900">
                            {CITY_LABELS[policy.city_tier] || policy.city_tier}
                          </td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 capitalize">
                            <span className="inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 font-semibold border border-blue-200">
                              {policy.flight_class?.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-semibold text-slate-900 whitespace-nowrap">₹{parseFloat(policy.max_hotel_rate_per_night).toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-semibold text-slate-900 whitespace-nowrap">₹{parseFloat(policy.per_diem_rate).toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-semibold text-slate-900 whitespace-nowrap">₹{parseFloat(policy.max_meal_per_day).toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-semibold text-slate-900 whitespace-nowrap">₹{parseFloat(policy.max_local_transit).toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              <button onClick={() => handleEdit(policy)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="Edit Policy">
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleDeletePolicyClick(policy.id)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                                title="Delete policy tier">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )
        ))
      )}

      {!loading && !policies.length && (
        <div className="text-center py-16">
          <p className="text-slate-400 text-sm">No policy tiers configured yet</p>
          <p className="text-slate-400 text-xs mt-1">Add policy tiers to enable expense policy checking</p>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeletePolicyModal} onOpenChange={setShowDeletePolicyModal}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <div className="w-11 h-11 rounded-full bg-red-100 text-red-600 flex items-center justify-center mb-1 mx-auto sm:mx-0">
              <Trash2 className="w-5 h-5" />
            </div>
            <AlertDialogTitle className="text-lg sm:text-xl font-bold text-slate-900">
              Delete Policy Tier
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-600">
              Are you sure you want to delete this policy tier rule? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4 gap-2 sm:gap-0">
            <AlertDialogCancel
              onClick={() => setShowDeletePolicyModal(false)}
              className="border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeletePolicy}
              className="bg-red-600 hover:bg-red-700 text-white font-medium"
            >
              Delete Policy
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
